import clsx from "clsx";
import Link from "next/link";
import Logo from "@/components/Logo";
import { Icon, type IconNameExtended } from "@/components/library/Icon";
import { BookDemoButton } from "@/puck/BookDemoButton";
import {
  BUILDER_ICON_TYPE,
  BuilderHeroBlock,
  IconPickerField,
} from "@/puck/clientBlocks";
import { studioLinkField } from "@/puck/fields/studioLinkField";
import type { LandingComponents } from "@/puck/blocks/blockTypes";
import type { FeatureStripBlockProps } from "@/puck/types";

function FeatureStrip({ items }: Readonly<FeatureStripBlockProps>) {
  return (
    <section className="w-full bg-white">
      <div className="mx-auto grid w-full max-w-6xl grid-cols-1 sm:grid-cols-2 md:grid-cols-4">
        {items.map((feature, index) => (
          <div
            key={`${feature.text}-${index}`}
            className={clsx(
              "px-6 py-10",
              index > 0 && "md:border-l md:border-black/10",
            )}
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-gail-primary-light text-gail-primary">
              {feature.icon ? (
                <Icon
                  icon={feature.icon as IconNameExtended}
                  type={BUILDER_ICON_TYPE}
                  className="h-5 w-5"
                />
              ) : null}
            </span>
            <p className="mt-4 font-monument text-sm leading-snug text-black md:text-[15px]">
              {feature.text}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

export const structureConfigs: Pick<
  LandingComponents,
  "Header" | "Hero" | "FeatureStrip"
> = {
  Header: {
    label: "Landing header",
    fields: {
      ctaLabel: { type: "text", label: "CTA label" },
      ctaHref: studioLinkField("CTA link"),
    },
    defaultProps: {
      ctaLabel: "Book a demo",
      ctaHref: "#book-demo",
    },
    render: ({ ctaLabel, ctaHref }) => (
      <header className="sticky top-0 z-40 w-full border-b border-transparent bg-marketing-surface/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link href="/" className="shrink-0" aria-label="Gail home">
            <Logo className="h-6 w-auto" />
          </Link>
          <BookDemoButton href={ctaHref}>{ctaLabel}</BookDemoButton>
        </div>
      </header>
    ),
  },
  Hero: {
    label: "Hero",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "textarea", contentEditable: true },
      accent: { type: "text", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      primaryCtaLabel: { type: "text", label: "Primary CTA label" },
      primaryCtaHref: studioLinkField("Primary CTA link"),
      secondaryCtaLabel: { type: "text", label: "Secondary CTA label" },
      secondaryCtaHref: studioLinkField("Secondary CTA link"),
      showVideo: {
        type: "radio",
        label: "Hero video",
        options: [
          { label: "Show", value: true },
          { label: "Hide", value: false },
        ],
      },
      videoMediaId: {
        type: "text",
        label: "Wistia media ID (from the video's share/embed URL)",
      },
      videoAutoplay: {
        type: "radio",
        label: "Video autoplay",
        options: [
          { label: "Off", value: false },
          { label: "On muted", value: true },
        ],
      },
      footnotes: {
        type: "array",
        label: "Footnotes",
        min: 0,
        max: 5,
        getItemSummary: (item) => item.text,
        defaultItemProps: { text: "Proof point" },
        arrayFields: {
          text: { type: "text", contentEditable: true },
        },
      },
    },
    defaultProps: {
      eyebrow: "AI landing page",
      title: "Launch your next campaign with Gail",
      accent: "in every language",
      subtitle:
        "Compose a high-converting page from Gail's proven landing sections, translate it, and publish in minutes.",
      primaryCtaLabel: "Book a demo",
      primaryCtaHref: "#book-demo",
      secondaryCtaLabel: "",
      secondaryCtaHref: "#workflows",
      showVideo: true,
      videoMediaId: "",
      videoAutoplay: false,
      footnotes: [
        { text: "Recover more without adding headcount" },
        { text: "Always-on outreach" },
        { text: "Built for compliant teams" },
      ],
    },
    render: ({ puck, ...props }) => (
      <BuilderHeroBlock {...props} isEditing={puck.isEditing} />
    ),
  },
  FeatureStrip: {
    label: "Feature strip",
    fields: {
      items: {
        type: "array",
        min: 1,
        max: 4,
        getItemSummary: (item) => item.text,
        defaultItemProps: {
          icon: "circle-check",
          text: "Editable proof point",
        },
        arrayFields: {
          icon: {
            type: "custom",
            label: "Icon",
            render: ({ value, onChange }) => (
              <IconPickerField value={value} onChange={onChange} />
            ),
          },
          text: { type: "text", contentEditable: true },
        },
      },
    },
    defaultProps: {
      items: [
        {
          icon: "shield-halved",
          text: "Compliant across every customer touchpoint",
        },
        { icon: "message-lines", text: "Voice, SMS, and email in one workflow" },
        {
          icon: "calendar-check",
          text: "Book qualified next steps automatically",
        },
        {
          icon: "wand-magic-sparkles",
          text: "AI tuned to your brand and process",
        },
      ],
    },
    render: FeatureStrip,
  },
};
